import type { TFunction } from "i18next";

/**
 * Translate an Axios/network error thrown during authentication into a
 * clear, user-friendly message.
 *
 * Priority:
 * 1. Network / timeout errors (no HTTP response reached us)
 * 2. Explicit message returned by the backend (e.g. "Invalid credentials")
 * 3. Status-code based fallbacks (rate limiting, server errors)
 * 4. Generic fallback
 *
 * Field-level validation errors are returned separately under `fieldErrors`
 * so the caller can attach them to the matching form inputs.
 */
export interface ParsedAuthError {
  message: string;
  fieldErrors: Record<string, string>;
}

export const parseAuthError = (
  e: any,
  t: TFunction,
): ParsedAuthError => {
  const fieldErrors: Record<string, string> =
    e?.response?.data?.meta?.errors || {};

  // No response object => request never reached the server.
  if (!e?.response) {
    if (e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "")) {
      return { message: t("auth:errors.timeout"), fieldErrors };
    }
    return { message: t("auth:errors.network"), fieldErrors };
  }

  const status = e.response.status;
  const backendMessage = e.response.data?.message;

  // Rate limiting
  if (status === 429) {
    return {
      message: backendMessage || t("auth:errors.tooManyRequests"),
      fieldErrors,
    };
  }

  // Bad credentials / bad request / unauthorized: prefer the backend message
  // which is already specific (e.g. "Invalid credentials"), otherwise use a
  // friendly invalid-credentials message.
  if (status === 400 || status === 401) {
    return {
      message: backendMessage || t("auth:errors.invalidCredentials"),
      fieldErrors,
    };
  }

  // Server-side failures
  if (status >= 500) {
    return { message: t("auth:errors.serverError"), fieldErrors };
  }

  return {
    message: backendMessage || t("auth:errors.generic"),
    fieldErrors,
  };
};
